import React, { useState, useEffect, useCallback } from 'react';
import { apiClient } from '../../../api/client';
import {
  PRIMARY, TEXT_DARK, TEXT_MUTED, BORDER_LIGHT, BG_SURFACE,
  FONT_HEADING,
} from './designTokens';

interface SeatingAlert {
  id: string;
  ruleId: string;
  severity: 'critical' | 'warning' | 'recommendation';
  title: string;
  description: string;
  affectedGuestIds: string[];
  affectedTableIds: string[];
  priority: number;
  metadata: Record<string, any>;
}

interface AlertsResponse {
  alerts: SeatingAlert[];
  counts: { critical: number; warning: number; recommendation: number };
}

interface HealthScore {
  overall: number;
  tierCompliance: number;
  capacityBalance: number;
}

const SEVERITY_CONFIG = {
  critical: { icon: '🔴', label: 'Critical', color: '#dc2626', bg: '#fef2f2' },
  warning: { icon: '🟡', label: 'Warning', color: '#d97706', bg: '#fffbeb' },
  recommendation: { icon: '🟢', label: 'Recommendation', color: '#059669', bg: '#ecfdf5' },
};

const styles: Record<string, React.CSSProperties> = {
  container: { maxWidth: '900px', padding: '0 0.5rem' },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '1.25rem',
    gap: '0.5rem',
  },
  title: {
    fontSize: 'clamp(0.95rem, 3vw, 1.1rem)',
    fontFamily: FONT_HEADING,
    fontWeight: 700,
    color: TEXT_DARK,
    margin: 0,
  },
  scanBtn: {
    padding: '0.5rem 1rem',
    fontSize: '0.82rem',
    fontWeight: 600,
    backgroundColor: PRIMARY,
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    whiteSpace: 'nowrap' as const,
  },
  scoreRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
    gap: '0.75rem',
    marginBottom: '1.25rem',
  },
  scoreCard: {
    padding: '0.75rem 0.5rem',
    backgroundColor: BG_SURFACE,
    border: `1px solid ${BORDER_LIGHT}`,
    borderRadius: '8px',
    textAlign: 'center' as const,
  },
  scoreValue: {
    fontSize: 'clamp(1.2rem, 4vw, 1.6rem)',
    fontFamily: FONT_HEADING,
    fontWeight: 700,
    color: TEXT_DARK,
  },
  scoreLabel: {
    fontSize: '0.68rem',
    color: TEXT_MUTED,
    textTransform: 'uppercase' as const,
    marginTop: '0.2rem',
  },
  countsRow: {
    display: 'flex',
    gap: '0.5rem',
    marginBottom: '1rem',
    flexWrap: 'wrap' as const,
  },
  countBadge: {
    padding: '0.3rem 0.6rem',
    borderRadius: '12px',
    fontSize: '0.72rem',
    fontWeight: 600,
    whiteSpace: 'nowrap' as const,
  },
  alertList: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '0.5rem',
  },
  alertCard: {
    padding: '0.65rem 0.75rem',
    borderRadius: '6px',
    border: `1px solid ${BORDER_LIGHT}`,
    display: 'flex',
    gap: '0.5rem',
    alignItems: 'flex-start',
  },
  alertIcon: {
    fontSize: '0.9rem',
    lineHeight: 1,
    marginTop: '0.1rem',
    flexShrink: 0,
  },
  alertContent: {
    flex: 1,
    minWidth: 0,
  },
  alertTitle: {
    fontSize: '0.82rem',
    fontWeight: 600,
    color: TEXT_DARK,
    margin: 0,
    wordBreak: 'break-word' as const,
  },
  alertDesc: {
    fontSize: '0.75rem',
    color: TEXT_MUTED,
    margin: '0.2rem 0 0',
    lineHeight: 1.4,
    wordBreak: 'break-word' as const,
  },
  alertNote: {
    fontSize: '0.75rem',
    color: '#1d4ed8',
    fontWeight: 700,
    margin: '0.35rem 0 0',
    lineHeight: 1.4,
    wordBreak: 'break-word' as const,
  },
  alertRecommendation: {
    fontSize: '0.75rem',
    color: '#1a6b3c',
    fontWeight: 600,
    fontStyle: 'italic' as const,
    margin: '0.25rem 0 0',
    lineHeight: 1.4,
    wordBreak: 'break-word' as const,
  },
  emptyState: {
    padding: '2rem 1rem',
    textAlign: 'center' as const,
    color: TEXT_MUTED,
    fontSize: '0.9rem',
  },
  loading: {
    padding: '2rem 1rem',
    textAlign: 'center' as const,
    color: TEXT_MUTED,
    fontSize: '0.85rem',
  },
  fixBtn: {
    padding: '0.3rem 0.7rem',
    fontSize: '0.72rem',
    fontWeight: 600,
    backgroundColor: PRIMARY,
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    cursor: 'pointer',
    marginLeft: '0.4rem',
  },
  fixBtnDisabled: {
    padding: '0.3rem 0.7rem',
    fontSize: '0.72rem',
    fontWeight: 600,
    backgroundColor: '#9ca3af',
    color: '#fff',
    border: 'none',
    borderRadius: '4px',
    cursor: 'not-allowed',
    marginLeft: '0.4rem',
  },
  previewBtn: {
    padding: '0.3rem 0.7rem',
    fontSize: '0.72rem',
    fontWeight: 600,
    backgroundColor: '#fff',
    color: '#1e40af',
    border: '1px solid #bfdbfe',
    borderRadius: '4px',
    cursor: 'pointer',
  },
};

export function SeatingIntelligencePanel() {
  const [alerts, setAlerts] = useState<SeatingAlert[]>([]);
  const [counts, setCounts] = useState<{ critical: number; warning: number; recommendation: number }>({ critical: 0, warning: 0, recommendation: 0 });
  const [score, setScore] = useState<HealthScore | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [applyingAlertId, setApplyingAlertId] = useState<string | null>(null);
  const [applyResult, setApplyResult] = useState<{ alertId: string; message: string; success: boolean } | null>(null);
  const [previewAlertId, setPreviewAlertId] = useState<string | null>(null);
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewData, setPreviewData] = useState<{ alertId: string; moves: Array<{ guestId: string; fromSeatId: string; toSeatId: string; guestName: string; fromTableName: string; toTableName: string; toZoneName: string }> } | null>(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [alertsRes, scoreRes] = await Promise.all([
        apiClient<AlertsResponse>('/seating-intelligence/alerts'),
        apiClient<HealthScore>('/seating-intelligence/health-score'),
      ]);
      setAlerts(alertsRes.alerts);
      setCounts(alertsRes.counts);
      setScore(scoreRes);
    } catch (err: unknown) {
      const e = err as { message?: string };
      setError(e.message || 'Failed to load seating intelligence data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  async function handleFixIt(alert: SeatingAlert) {
    setApplyingAlertId(alert.id);
    setApplyResult(null);
    try {
      // Use previewed data if available, otherwise fetch fresh
      let moves = previewData?.alertId === alert.id ? previewData.moves : null;
      if (!moves) {
        const params = new URLSearchParams({ ruleId: alert.ruleId });
        if (alert.metadata.guestName) params.set('guestName', alert.metadata.guestName);
        if (alert.metadata.ticketNumber) params.set('ticketNumber', alert.metadata.ticketNumber);
        if (alert.metadata.tierName) params.set('tierName', alert.metadata.tierName);
        if (alert.metadata.chapterAffiliation) params.set('chapterAffiliation', alert.metadata.chapterAffiliation);
        if (alert.metadata.expectedZone) params.set('expectedZone', alert.metadata.expectedZone);

        const recRes = await apiClient<{ suggestions: Array<{ guestId: string; fromSeatId: string; toSeatId: string; guestName: string; fromTableName: string; toTableName: string; toZoneName: string; reason?: string }> }>(
          `/seating-intelligence/recommendations?${params.toString()}`
        );

        if (!recRes.suggestions || recRes.suggestions.length === 0) {
          setApplyResult({ alertId: alert.id, message: 'No available seats found in the target zone.', success: false });
          return;
        }
        if (recRes.suggestions[0].toSeatId === '') {
          setApplyResult({ alertId: alert.id, message: recRes.suggestions[0].reason || 'Cannot auto-fix: reserved group.', success: false });
          return;
        }
        moves = recRes.suggestions;
      }

      // Apply all moves sequentially
      let successCount = 0;
      for (const move of moves) {
        const result = await apiClient<{ success: boolean; message: string }>(
          '/seating-intelligence/apply-move',
          { method: 'POST', body: { guestId: move.guestId, fromSeatId: move.fromSeatId, toSeatId: move.toSeatId } },
        );
        if (result.success) successCount++;
      }

      setApplyResult({
        alertId: alert.id,
        message: `Moved ${successCount} of ${moves.length} guest(s) successfully.`,
        success: successCount > 0,
      });
      setPreviewData(null);
      setPreviewAlertId(null);

      if (successCount > 0) {
        setTimeout(() => fetchData(), 500);
      }
    } catch (err: unknown) {
      const e = err as { message?: string };
      setApplyResult({ alertId: alert.id, message: e.message || 'Failed to apply fix.', success: false });
    } finally {
      setApplyingAlertId(null);
    }
  }

  async function handlePreview(alert: SeatingAlert) {
    setPreviewAlertId(alert.id);
    setPreviewLoading(true);
    setPreviewData(null);
    setApplyResult(null);
    try {
      const params = new URLSearchParams({ ruleId: alert.ruleId });
      if (alert.metadata.guestName) params.set('guestName', alert.metadata.guestName);
      if (alert.metadata.ticketNumber) params.set('ticketNumber', alert.metadata.ticketNumber);
      if (alert.metadata.tierName) params.set('tierName', alert.metadata.tierName);
      if (alert.metadata.chapterAffiliation) params.set('chapterAffiliation', alert.metadata.chapterAffiliation);
      if (alert.metadata.expectedZone) params.set('expectedZone', alert.metadata.expectedZone);

      const recRes = await apiClient<{ suggestions: Array<{ guestId: string; fromSeatId: string; toSeatId: string; guestName: string; fromTableName: string; toTableName: string; toZoneName: string; reason?: string }> }>(
        `/seating-intelligence/recommendations?${params.toString()}`
      );

      if (!recRes.suggestions || recRes.suggestions.length === 0) {
        setPreviewData(null);
        setApplyResult({ alertId: alert.id, message: 'No available seats in the target zone.', success: false });
      } else if (recRes.suggestions[0].toSeatId === '') {
        // RES group — cannot auto-fix
        setPreviewData(null);
        setApplyResult({ alertId: alert.id, message: recRes.suggestions[0].reason || 'Cannot auto-fix: reserved group.', success: false });
      } else {
        setPreviewData({ alertId: alert.id, moves: recRes.suggestions });
      }
    } catch (err: unknown) {
      const e = err as { message?: string };
      setApplyResult({ alertId: alert.id, message: e.message || 'Failed to load preview.', success: false });
    } finally {
      setPreviewLoading(false);
    }
  }

  function getScoreColor(value: number): string {
    if (value >= 90) return '#059669';
    if (value >= 70) return '#d97706';
    return '#dc2626';
  }

  if (loading) {
    return <div style={styles.loading}>Analyzing seating ecosystem...</div>;
  }

  if (error) {
    return (
      <div style={styles.container}>
        <div style={{ padding: '1rem', backgroundColor: '#fef2f2', border: '1px solid #fca5a5', borderRadius: '6px', color: '#b91c1c', fontSize: '0.85rem' }}>
          {error}
        </div>
      </div>
    );
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h3 style={styles.title}>🧠 Seating Intelligence</h3>
        <button style={styles.scanBtn} onClick={fetchData}>
          Scan Now
        </button>
      </div>

      {/* Health Score Cards */}
      {score && (
        <div style={styles.scoreRow}>
          <div style={styles.scoreCard}>
            <div style={{ ...styles.scoreValue, color: getScoreColor(score.overall) }}>
              {score.overall}
            </div>
            <div style={styles.scoreLabel}>Overall Health</div>
          </div>
          <div style={styles.scoreCard}>
            <div style={{ ...styles.scoreValue, color: getScoreColor(score.tierCompliance) }}>
              {score.tierCompliance}%
            </div>
            <div style={styles.scoreLabel}>Tier Compliance</div>
          </div>
          <div style={styles.scoreCard}>
            <div style={{ ...styles.scoreValue, color: getScoreColor(score.capacityBalance) }}>
              {score.capacityBalance}%
            </div>
            <div style={styles.scoreLabel}>Capacity Balance</div>
          </div>
        </div>
      )}

      {/* Alert Counts */}
      <div style={styles.countsRow}>
        <span style={{ ...styles.countBadge, backgroundColor: SEVERITY_CONFIG.critical.bg, color: SEVERITY_CONFIG.critical.color }}>
          {SEVERITY_CONFIG.critical.icon} {counts.critical} Critical
        </span>
        <span style={{ ...styles.countBadge, backgroundColor: SEVERITY_CONFIG.warning.bg, color: SEVERITY_CONFIG.warning.color }}>
          {SEVERITY_CONFIG.warning.icon} {counts.warning} Warnings
        </span>
        <span style={{ ...styles.countBadge, backgroundColor: SEVERITY_CONFIG.recommendation.bg, color: SEVERITY_CONFIG.recommendation.color }}>
          {SEVERITY_CONFIG.recommendation.icon} {counts.recommendation} Recommendations
        </span>
      </div>

      {/* Alert List */}
      {alerts.length === 0 ? (
        <div style={styles.emptyState}>
          ✅ No seating issues detected. Everything looks good!
        </div>
      ) : (
        <div style={styles.alertList}>
          {alerts.map((alert) => {
            const config = SEVERITY_CONFIG[alert.severity];
            const isActionable = alert.ruleId === 'tier-mismatch' || alert.ruleId === 'chapter-zone-mismatch';
            const isApplying = applyingAlertId === alert.id;
            const result = applyResult?.alertId === alert.id ? applyResult : null;
            return (
              <div
                key={alert.id}
                style={{ ...styles.alertCard, backgroundColor: config.bg, borderColor: config.color + '33' }}
              >
                <span style={styles.alertIcon}>{config.icon}</span>
                <div style={styles.alertContent}>
                  <p style={styles.alertTitle}>{alert.title}</p>
                  {alert.description.split('\n').map((line, i) => {
                    const isNote = line.startsWith('Note:');
                    const isRecommendation = line.startsWith('Recommendation:');
                    const lineStyle = isNote
                      ? styles.alertNote
                      : isRecommendation
                        ? styles.alertRecommendation
                        : styles.alertDesc;
                    return <p key={i} style={lineStyle}>{line}</p>;
                  })}
                  {isActionable && (
                    <div style={{ marginTop: '0.5rem' }}>
                      {/* Preview area */}
                      {previewData?.alertId === alert.id && (
                        <div style={{ marginBottom: '0.4rem', padding: '0.4rem 0.5rem', backgroundColor: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: '4px', fontSize: '0.72rem', color: '#1e40af' }}>
                          <strong>Proposed move{previewData.moves.length > 1 ? `s (${previewData.moves.length} guests)` : ''}:</strong>
                          {previewData.moves.map((m, i) => (
                            <div key={i} style={{ marginTop: '0.2rem' }}>
                              {m.guestName}: {m.fromTableName} → {m.toTableName} ({m.toZoneName})
                            </div>
                          ))}
                        </div>
                      )}
                      {previewLoading && previewAlertId === alert.id && (
                        <div style={{ marginBottom: '0.4rem', fontSize: '0.72rem', color: '#6b7280' }}>Loading preview...</div>
                      )}
                      <button
                        style={styles.previewBtn}
                        onClick={() => handlePreview(alert)}
                        disabled={previewLoading && previewAlertId === alert.id}
                        aria-label={`Preview fix for: ${alert.title}`}
                      >
                        👁️ Preview
                      </button>
                      <button
                        style={isApplying ? styles.fixBtnDisabled : styles.fixBtn}
                        disabled={isApplying}
                        onClick={() => handleFixIt(alert)}
                        aria-label={`Fix: ${alert.title}`}
                      >
                        {isApplying ? '⏳ Fixing...' : '⚡ Fix It'}
                      </button>
                      {result && (
                        <span style={{ marginLeft: '0.5rem', fontSize: '0.72rem', color: result.success ? '#059669' : '#dc2626', fontWeight: 600 }}>
                          {result.message}
                        </span>
                      )}
                    </div>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
